const IEM_SBW_URL = "https://mesonet.agron.iastate.edu/geojson/sbw.py";
const IEM_WATCH_URL = "https://mesonet.agron.iastate.edu/json/spcwatch.py";
const IEM_MCD_URL = "https://mesonet.agron.iastate.edu/api/1/nws/spc_mcd.geojson";

export const ALERT_RANGE_PAD = 1.1;
export const ALERT_MAX_FEATURES = 200;

const WARNING_SIGS = new Set(["W", "A"]);

const PHENOMENA_NAMES = {
  TO: "Tornado",
  SV: "Severe Thunderstorm",
  FF: "Flash Flood",
  FA: "Flood",
  FL: "Flood",
  MA: "Special Marine",
  SQ: "Snow Squall",
  EW: "Extreme Wind",
  DS: "Dust Storm",
  WS: "Winter Storm",
  BZ: "Blizzard",
  IS: "Ice Storm",
  HW: "High Wind",
  TR: "Tropical Storm",
  HU: "Hurricane",
  TY: "Typhoon",
  TS: "Tsunami",
};

const SIGNIFICANCE_NAMES = {
  W: "Warning",
  A: "Watch",
  Y: "Advisory",
  S: "Statement",
};

const STYLE = {
  toWarn: { stroke: "#e31a1c", fill: "#e31a1c", fillOpacity: 0.20, weight: 2.5 },
  svWarn: { stroke: "#ffaa00", fill: "#ffaa00", fillOpacity: 0.16, weight: 2 },
  ffWarn: { stroke: "#00c853", fill: "#00c853", fillOpacity: 0.14, weight: 2 },
  flWarn: { stroke: "#2e7d32", fill: "#2e7d32", fillOpacity: 0.12, weight: 2 },
  maWarn: { stroke: "#ff7f00", fill: "#ff7f00", fillOpacity: 0.14, weight: 2 },
  sqWarn: { stroke: "#ff69b4", fill: "#ff69b4", fillOpacity: 0.14, weight: 2 },
  ewWarn: { stroke: "#b10026", fill: "#b10026", fillOpacity: 0.18, weight: 2.5 },
  otherWarn: { stroke: "#ff6600", fill: "#ff6600", fillOpacity: 0.12, weight: 2 },
  toWatch: { stroke: "#ff6688", fill: "#ff6688", fillOpacity: 0.08, weight: 2, dashArray: "8 6" },
  svWatch: { stroke: "#e6c200", fill: "#e6c200", fillOpacity: 0.08, weight: 2, dashArray: "8 6" },
  md: { stroke: "#c44cff", fill: "#c44cff", fillOpacity: 0.06, weight: 2, dashArray: "7 5" },
};

export function parseUtcScanMs(date, time) {
  const d = String(date || "").replace(/-/g, "");
  const t = String(time || "").replace(/:/g, "");
  if (d.length < 8 || t.length < 4) return null;
  const y = parseInt(d.slice(0, 4), 10);
  const mo = parseInt(d.slice(4, 6), 10) - 1;
  const da = parseInt(d.slice(6, 8), 10);
  const hh = parseInt(t.slice(0, 2), 10);
  const mm = parseInt(t.slice(2, 4), 10);
  const ss = t.length >= 6 ? parseInt(t.slice(4, 6), 10) : 0;
  const ms = Date.UTC(y, mo, da, hh, mm, ss);
  return Number.isFinite(ms) ? ms : null;
}

export function parseAlertTime(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number") {
    if (!Number.isFinite(value) || value < 0) return null;
    return value > 1e12 ? value : value * 1000;
  }
  const s = String(value).trim();
  if (/^\d{12}$/.test(s)) {
    return Date.UTC(
      parseInt(s.slice(0, 4), 10),
      parseInt(s.slice(4, 6), 10) - 1,
      parseInt(s.slice(6, 8), 10),
      parseInt(s.slice(8, 10), 10),
      parseInt(s.slice(10, 12), 10),
    );
  }
  const ms = Date.parse(s.includes("T") ? s : s.replace(" ", "T"));
  return Number.isFinite(ms) ? ms : null;
}

export function isoFromScanMs(ms) {
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toISOString().replace(/\.\d{3}Z$/, "Z");
}

export function compactUtcMinute(ms) {
  if (!Number.isFinite(ms)) return null;
  const dt = new Date(ms);
  return [
    dt.getUTCFullYear(),
    String(dt.getUTCMonth() + 1).padStart(2, "0"),
    String(dt.getUTCDate()).padStart(2, "0"),
    String(dt.getUTCHours()).padStart(2, "0"),
    String(dt.getUTCMinutes()).padStart(2, "0"),
  ].join("");
}

export function haversineKm(lat1, lon1, lat2, lon2) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = (Math.sin(dLat / 2) ** 2)
    + (Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * (Math.sin(dLon / 2) ** 2));
  return 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(a)));
}

export function walkPositions(geometry, visit) {
  if (!geometry) return;
  const type = geometry.type;
  if (type === "Point") {
    visit(geometry.coordinates);
    return;
  }
  if (type === "Polygon") {
    for (const ring of geometry.coordinates || []) {
      for (const pos of ring) visit(pos);
    }
    return;
  }
  if (type === "MultiPolygon") {
    for (const poly of geometry.coordinates || []) {
      for (const ring of poly) {
        for (const pos of ring) visit(pos);
      }
    }
  }
}

export function walkRings(geometry, visit) {
  if (!geometry) return;
  if (geometry.type === "Polygon") {
    for (const ring of geometry.coordinates || []) visit(ring);
    return;
  }
  if (geometry.type === "MultiPolygon") {
    for (const poly of geometry.coordinates || []) {
      for (const ring of poly) visit(ring);
    }
  }
}

export function geometryBbox(geometry) {
  let south = Infinity;
  let west = Infinity;
  let north = -Infinity;
  let east = -Infinity;
  let found = false;
  walkPositions(geometry, (pos) => {
    const lon = Number(pos?.[0]);
    const lat = Number(pos?.[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
    found = true;
    if (lat < south) south = lat;
    if (lat > north) north = lat;
    if (lon < west) west = lon;
    if (lon > east) east = lon;
  });
  return found ? { south, west, north, east } : null;
}

export function circleBbox(lat, lon, radiusKm) {
  const dlat = radiusKm / 111.32;
  const cos = Math.cos((lat * Math.PI) / 180);
  const dlon = radiusKm / (111.32 * Math.max(0.2, Math.abs(cos)));
  return {
    south: lat - dlat,
    north: lat + dlat,
    west: lon - dlon,
    east: lon + dlon,
  };
}

export function bboxesOverlap(a, b) {
  if (!a || !b) return false;
  return a.west <= b.east && a.east >= b.west && a.south <= b.north && a.north >= b.south;
}

export function pointInRing(lat, lon, ring) {
  if (!ring || ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = Number(ring[i][0]);
    const yi = Number(ring[i][1]);
    const xj = Number(ring[j][0]);
    const yj = Number(ring[j][1]);
    if (!Number.isFinite(xi) || !Number.isFinite(yi) || !Number.isFinite(xj) || !Number.isFinite(yj)) {
      continue;
    }
    const crosses = ((yi > lat) !== (yj > lat))
      && (lon < ((xj - xi) * (lat - yi)) / ((yj - yi) || 1e-12) + xi);
    if (crosses) inside = !inside;
  }
  return inside;
}

function pointInPolygonCoords(lat, lon, rings) {
  if (!rings?.length) return false;
  if (!pointInRing(lat, lon, rings[0])) return false;
  for (let i = 1; i < rings.length; i++) {
    if (pointInRing(lat, lon, rings[i])) return false;
  }
  return true;
}

export function pointInGeometry(lat, lon, geometry) {
  if (!geometry) return false;
  if (geometry.type === "Polygon") return pointInPolygonCoords(lat, lon, geometry.coordinates);
  if (geometry.type === "MultiPolygon") {
    return (geometry.coordinates || []).some((rings) => pointInPolygonCoords(lat, lon, rings));
  }
  return false;
}

function distPointToSegKm(lat, lon, a, b) {
  const lon1 = Number(a?.[0]);
  const lat1 = Number(a?.[1]);
  const lon2 = Number(b?.[0]);
  const lat2 = Number(b?.[1]);
  if (![lon1, lat1, lon2, lat2].every(Number.isFinite)) return Infinity;
  const kx = 111.32 * Math.cos((lat * Math.PI) / 180);
  const ky = 111.32;
  const ax = lon1 * kx;
  const ay = lat1 * ky;
  const bx = lon2 * kx;
  const by = lat2 * ky;
  const px = lon * kx;
  const py = lat * ky;
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = (dx * dx) + (dy * dy);
  const t = len2 <= 1e-9 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
  const qx = ax + (t * dx);
  const qy = ay + (t * dy);
  return Math.hypot(px - qx, py - qy);
}

export function geometryIntersectsRange(geometry, lat, lon, rangeKm) {
  if (!geometry || !Number.isFinite(lat) || !Number.isFinite(lon)) return false;
  const limit = Number(rangeKm);
  if (!Number.isFinite(limit) || limit <= 0) return false;
  const bbox = geometryBbox(geometry);
  if (!bbox || !bboxesOverlap(bbox, circleBbox(lat, lon, limit))) return false;
  if (pointInGeometry(lat, lon, geometry)) return true;
  let hit = false;
  walkPositions(geometry, (pos) => {
    if (hit) return;
    const plat = Number(pos?.[1]);
    const plon = Number(pos?.[0]);
    if (Number.isFinite(plat) && Number.isFinite(plon) && haversineKm(lat, lon, plat, plon) <= limit) {
      hit = true;
    }
  });
  if (hit) return true;
  walkRings(geometry, (ring) => {
    if (hit) return;
    for (let i = 0; i < ring.length - 1; i++) {
      if (distPointToSegKm(lat, lon, ring[i], ring[i + 1]) <= limit) {
        hit = true;
        return;
      }
    }
  });
  return hit;
}

export function geometryLabelPoint(geometry) {
  let sumLat = 0;
  let sumLon = 0;
  let n = 0;
  const firstRing = [];
  if (geometry?.type === "Polygon") {
    firstRing.push(...(geometry.coordinates?.[0] || []));
  } else if (geometry?.type === "MultiPolygon") {
    firstRing.push(...(geometry.coordinates?.[0]?.[0] || []));
  }
  for (const pos of firstRing) {
    const lon = Number(pos?.[0]);
    const lat = Number(pos?.[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    sumLat += lat;
    sumLon += lon;
    n += 1;
  }
  if (!n) return null;
  return [sumLat / n, sumLon / n];
}

export function isActiveAt(feature, scanMs) {
  if (!Number.isFinite(scanMs)) return true;
  const start = parseAlertTime(feature.issue || feature.onset || feature.polygonBegin);
  const end = parseAlertTime(feature.expire || feature.polygonEnd);
  if (start != null && scanMs < start) return false;
  if (end != null && scanMs >= end) return false;
  return true;
}

export function filterAlertsInEffect(features, scanMs) {
  return (features || []).filter((feature) => isActiveAt(feature, scanMs));
}

export function filterAlertsInRange(features, lat, lon, maxRangeKm, pad = ALERT_RANGE_PAD) {
  const base = Number(maxRangeKm);
  const limit = (Number.isFinite(base) && base > 0 ? base : 230) * (Number(pad) || ALERT_RANGE_PAD);
  return (features || []).filter((feature) => geometryIntersectsRange(feature.geometry, lat, lon, limit));
}

function padEvent(id) {
  return String(id ?? "").padStart(4, "0");
}

export function phenomenaName(code) {
  return PHENOMENA_NAMES[String(code || "").toUpperCase()] || String(code || "").toUpperCase();
}

export function significanceName(code) {
  return SIGNIFICANCE_NAMES[String(code || "").toUpperCase()] || String(code || "").toUpperCase();
}

export function alertTitle(feature) {
  if (!feature) return "Alert";
  if (feature.kind === "md") {
    return `Mesoscale Discussion ${feature.number ?? ""}`.trim();
  }
  if (feature.kind === "watch") {
    const name = feature.type === "TOR" ? "Tornado Watch" : "Severe Thunderstorm Watch";
    const num = feature.number != null ? ` ${feature.number}` : "";
    return `${feature.isPds ? "PDS " : ""}${name}${num}`;
  }
  if (feature.ps) return feature.ps;
  const ph = phenomenaName(feature.phenomena);
  const sig = significanceName(feature.significance);
  return `${ph} ${sig}`.trim();
}

export function alertLabel(feature) {
  if (!feature) return "";
  if (feature.kind === "md") return `MD ${feature.number ?? ""}`.trim();
  if (feature.kind === "watch") {
    const kind = feature.type === "TOR" ? "WT" : "WS";
    return `WW ${feature.number ?? kind}`.trim();
  }
  const ph = String(feature.phenomena || "").toUpperCase();
  const short = ph === "TO" ? "TOR" : ph === "SV" ? "SVR" : ph === "FF" ? "FFW" : (ph || "WW");
  return feature.eventid != null ? `${short} ${feature.eventid}` : short;
}

export function alertStyle(feature) {
  if (!feature) return { ...STYLE.otherWarn };
  if (feature.kind === "md") return { ...STYLE.md };
  if (feature.kind === "watch" || feature.significance === "A") {
    if (feature.type === "TOR" || feature.phenomena === "TO") return { ...STYLE.toWatch };
    return { ...STYLE.svWatch };
  }
  const ph = String(feature.phenomena || "").toUpperCase();
  if (ph === "TO") return { ...STYLE.toWarn };
  if (ph === "SV") return { ...STYLE.svWarn };
  if (ph === "FF") return { ...STYLE.ffWarn };
  if (ph === "FL" || ph === "FA") return { ...STYLE.flWarn };
  if (ph === "MA") return { ...STYLE.maWarn };
  if (ph === "SQ") return { ...STYLE.sqWarn };
  if (ph === "EW") return { ...STYLE.ewWarn };
  return { ...STYLE.otherWarn };
}

export function sbwUrl(iso) {
  return `${IEM_SBW_URL}?ts=${encodeURIComponent(iso)}`;
}

export function watchUrl(compact) {
  return `${IEM_WATCH_URL}?ts=${encodeURIComponent(compact)}&fmt=geojson`;
}

export function mcdUrl(iso) {
  return `${IEM_MCD_URL}?valid=${encodeURIComponent(iso)}`;
}

function yearFromIso(value) {
  const ms = parseAlertTime(value);
  return Number.isFinite(ms) ? new Date(ms).getUTCFullYear() : null;
}

function firstProp(props, keys) {
  for (const key of keys) {
    if (props[key] != null && props[key] !== "") return props[key];
  }
  return null;
}

export function normalizeSbwFeature(raw) {
  const props = raw?.properties || {};
  const phenomena = String(props.phenomena || "").toUpperCase();
  const significance = String(props.significance || "").toUpperCase();
  if (!WARNING_SIGS.has(significance)) return null;
  if (!raw?.geometry) return null;
  const wfo = props.wfo ? String(props.wfo).toUpperCase() : null;
  const year = Number(props.year) || yearFromIso(props.issue);
  const eventid = props.eventid != null ? Number(props.eventid) : null;
  const href = props.href || (year && wfo && phenomena && eventid != null
    ? `https://mesonet.agron.iastate.edu/vtec/?year=${year}&wfo=K${wfo.replace(/^K/, "")}&phenomena=${phenomena}&significance=${significance}&eventid=${padEvent(eventid)}`
    : null);
  return {
    id: `sbw:${year || ""}:${wfo || ""}:${phenomena}:${significance}:${eventid ?? ""}`,
    kind: significance === "A" ? "watch" : "warning",
    phenomena,
    significance,
    type: phenomena === "TO" ? "TOR" : phenomena === "SV" ? "SVR" : phenomena,
    ps: props.ps || null,
    wfo,
    eventid: Number.isFinite(eventid) ? eventid : null,
    year,
    number: Number.isFinite(eventid) ? eventid : null,
    issue: props.issue || null,
    expire: props.expire || props.expire_utc || null,
    polygonBegin: props.polygon_begin || null,
    polygonEnd: props.polygon_end || null,
    href,
    concerning: null,
    hailtag: props.hailtag ?? null,
    windtag: props.windtag ?? null,
    tornadotag: props.tornadotag ?? null,
    isPds: !!(props.is_pds || props.max_is_pds),
    isEmergency: !!(props.is_emergency || props.max_is_emergency),
    geometry: raw.geometry,
  };
}

export function normalizeWatchFeature(raw) {
  const props = raw?.properties || {};
  if (!raw?.geometry) return null;
  const type = String(props.type || "").toUpperCase();
  const number = Number(firstProp(props, ["number", "num", "NUM"]));
  const issue = firstProp(props, ["issue", "utc_issued", "ISSUE"]);
  const expire = firstProp(props, ["expire", "utc_expired", "EXPIRE"]);
  const year = Number(props.year) || yearFromIso(issue);
  const href = props.spcurl || (year && Number.isFinite(number)
    ? `https://www.spc.noaa.gov/products/watch/${year}/ww${padEvent(number)}.html`
    : null);
  return {
    id: `ww:${year || ""}:${Number.isFinite(number) ? number : ""}`,
    kind: "watch",
    phenomena: type === "TOR" ? "TO" : "SV",
    significance: "A",
    type: type === "TOR" ? "TOR" : "SVR",
    ps: type === "TOR" ? "Tornado Watch" : "Severe Thunderstorm Watch",
    wfo: "SPC",
    eventid: Number.isFinite(number) ? number : null,
    year,
    number: Number.isFinite(number) ? number : null,
    issue: typeof issue === "string" && /^\d{12}$/.test(issue)
      ? isoFromScanMs(parseAlertTime(issue))
      : issue || null,
    expire: typeof expire === "string" && /^\d{12}$/.test(expire)
      ? isoFromScanMs(parseAlertTime(expire))
      : expire || null,
    polygonBegin: null,
    polygonEnd: null,
    href,
    concerning: null,
    hailtag: props.max_hail_size ?? props.MAX_HAIL ?? null,
    windtag: props.max_wind_gust_knots ?? props.MAX_GUST ?? null,
    tornadotag: null,
    isPds: !!(props.is_pds || props.IS_PDS),
    isEmergency: false,
    geometry: raw.geometry,
  };
}

export function normalizeMcdFeature(raw) {
  const props = raw?.properties || {};
  if (!raw?.geometry) return null;
  const number = Number(firstProp(props, ["num", "number", "product_num"]));
  const year = Number(props.year) || yearFromIso(props.issue);
  const href = year && Number.isFinite(number)
    ? `https://www.spc.noaa.gov/products/md/${year}/md${padEvent(number)}.html`
    : null;
  return {
    id: `md:${year || ""}:${Number.isFinite(number) ? number : ""}`,
    kind: "md",
    phenomena: "MD",
    significance: null,
    type: "MD",
    ps: "Mesoscale Discussion",
    wfo: "SPC",
    eventid: Number.isFinite(number) ? number : null,
    year,
    number: Number.isFinite(number) ? number : null,
    issue: props.issue || props.utc_issue || null,
    expire: props.expire || props.utc_expire || null,
    polygonBegin: null,
    polygonEnd: null,
    href,
    concerning: props.concerning || null,
    hailtag: props.most_prob_hail || null,
    windtag: props.most_prob_gust || null,
    tornadotag: props.most_prob_tornado || null,
    isPds: false,
    isEmergency: false,
    watchConfidence: props.watch_confidence ?? null,
    geometry: raw.geometry,
  };
}

export function featuresFromCollection(payload, normalize) {
  const list = Array.isArray(payload?.features) ? payload.features : [];
  const out = [];
  for (const raw of list) {
    const feature = normalize(raw);
    if (feature) out.push(feature);
  }
  return out;
}

function dedupeAlerts(features) {
  const seen = new Set();
  const out = [];
  for (const feature of features || []) {
    const key = feature.id || `${feature.kind}:${feature.year}:${feature.number}:${feature.phenomena}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(feature);
  }
  return out;
}

async function fetchJson(url, { signal } = {}) {
  const resp = await fetch(url, { signal });
  if (!resp.ok) throw new Error(`Alerts fetch failed (${resp.status})`);
  return resp.json();
}

function rethrowAbort(err) {
  if (err?.name === "AbortError") throw err;
  return err;
}

export async function loadAlerts({
  lat,
  lon,
  maxRangeKm,
  date,
  time,
  signal,
} = {}) {
  const scanMs = parseUtcScanMs(date, time);
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || scanMs == null) {
    return { features: [], reason: "empty" };
  }
  const iso = isoFromScanMs(scanMs);
  const compact = compactUtcMinute(scanMs);
  const settled = await Promise.allSettled([
    fetchJson(sbwUrl(iso), { signal }),
    fetchJson(watchUrl(compact), { signal }),
    fetchJson(mcdUrl(iso), { signal }),
  ]);
  if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
  for (const result of settled) {
    if (result.status === "rejected") rethrowAbort(result.reason);
  }

  const errors = [];
  const collected = [];
  const [sbwRes, watchRes, mcdRes] = settled;
  if (sbwRes.status === "fulfilled") {
    collected.push(...featuresFromCollection(sbwRes.value, normalizeSbwFeature));
  } else {
    errors.push(sbwRes.reason?.message || "SBW failed");
  }
  if (watchRes.status === "fulfilled") {
    collected.push(...featuresFromCollection(watchRes.value, normalizeWatchFeature));
  } else {
    errors.push(watchRes.reason?.message || "Watch failed");
  }
  if (mcdRes.status === "fulfilled") {
    collected.push(...featuresFromCollection(mcdRes.value, normalizeMcdFeature));
  } else {
    errors.push(mcdRes.reason?.message || "MCD failed");
  }

  if (errors.length === 3) {
    return { features: [], reason: "error", message: errors[0] };
  }

  const features = dedupeAlerts(
    filterAlertsInRange(filterAlertsInEffect(collected, scanMs), lat, lon, maxRangeKm),
  ).slice(0, ALERT_MAX_FEATURES);

  return { features, reason: "ok", partial: errors.length > 0 };
}
